<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Equipment</h1>
    <p class="!mt-0 text-muted-color">
      Manage equipment used in costing models.
    </p>
  </div>
  <p-button label="New Equipment" icon="pi pi-plus" (onClick)="openDrawer()" />
</div>

<div class="flex flex-wrap gap-3 items-end justify-between mb-4">
  <div class="flex flex-wrap gap-2 items-center">
    <p-iconfield>
      <p-inputicon styleClass="pi pi-search" />
      <input
        pInputText
        [formControl]="searchQuery"
        placeholder="Search equipment…"
        class="w-56"
      />
    </p-iconfield>

    @let typeOptions = typeFilterOptions$ | async;
    <p-multiselect
      [formControl]="selectedTypeFilters"
      [options]="typeOptions || []"
      optionLabel="label"
      optionValue="value"
      placeholder="Type"
      [showToggleAll]="false"
      [maxSelectedLabels]="2"
      styleClass="w-44"
    />

    @let marketOptions = marketFilterOptions$ | async;
    <p-multiselect
      [formControl]="selectedMarketFilters"
      [options]="marketOptions || []"
      optionLabel="label"
      optionValue="value"
      placeholder="Market"
      [showToggleAll]="false"
      [maxSelectedLabels]="2"
      styleClass="w-44"
    />

    @if (hasActiveFilters) {
      <p-button
        label="Clear filters"
        icon="pi pi-times"
        severity="secondary"
        size="small"
        [text]="true"
        (onClick)="clearFilters()"
      />
    }
  </div>

  <div class="flex items-center gap-3">
    <div class="flex items-center gap-2">
      <p-checkbox
        [formControl]="includeArchived"
        [binary]="true"
        inputId="includeArchivedEquipment"
      />
      <label for="includeArchivedEquipment" class="text-sm font-medium"
        >Include Archived Equipment</label
      >
    </div>
  </div>
</div>

@let equipmentRows = tableEquipment$ | async;
@let currencyCode = currencyCode$ | async;
@if (equipmentRows && equipmentRows.length > 0) {
  <div
    class="rounded-xl border border-surface-switch-200 shadow-sm overflow-x-auto"
  >
    <!-- Header Row -->
    <div
      class="hidden lg:grid rounded-t-xl bg-surface-switch-100 items-center gap-4 px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase border-b border-surface-switch-200"
      style="
        grid-template-columns: 2rem 1fr 2fr 1fr 1.5fr 1.5fr 1fr 1fr 1.5fr 1fr;
        min-width: 60rem;
      "
    >
      <div></div>
      <div>Type</div>
      <div>Name</div>
      <div>Market</div>
      <div>Supplier</div>
      <div>Source</div>
      <div>CapEx</div>
      <div>Amort</div>
      <div>Notes</div>
      <div class="text-center">Actions</div>
    </div>

    <div class="flex flex-col">
      @for (eq of equipmentRows; track eq.id) {
        <!-- Main Row -->
        <div
          class="border-b border-surface-switch-200 last:border-b-0 transition-colors duration-200"
          [class.bg-surface-switch-100]="expandedRows.has(eq.id)"
          [class.bg-surface-switch-50]="!expandedRows.has(eq.id)"
          [class.hover:bg-surface-switch-100]="!expandedRows.has(eq.id)"
        >
          <!-- Mobile Layout -->
          <div class="lg:hidden p-4 flex flex-col gap-2">
            <div class="flex justify-between items-start">
              <div>
                <span class="font-semibold text-lg">{{ eq.name }}</span>
                @if (eq.is_archived) {
                  <p-chip label="Archived" styleClass="ml-2 text-xs" />
                }
                @if (eq.equipment_type) {
                  <span class="ml-2 text-sm text-muted-color">{{
                    eq.equipment_type
                  }}</span>
                }
              </div>
              <div class="flex gap-1 shrink-0 ml-2">
                <p-button
                  [text]="true"
                  severity="secondary"
                  [rounded]="true"
                  size="small"
                  [icon]="
                    expandedRows.has(eq.id)
                      ? 'pi pi-chevron-down'
                      : 'pi pi-chevron-right'
                  "
                  (onClick)="toggleRow(eq.id)"
                />
                @if (!eq.is_archived) {
                  <p-button
                    icon="pi pi-pencil"
                    severity="secondary"
                    size="small"
                    [text]="true"
                    [rounded]="true"
                    (onClick)="openDrawer(eq)"
                    pTooltip="Edit"
                    tooltipPosition="top"
                  />
                  <p-button
                    icon="pi pi-box"
                    severity="secondary"
                    size="small"
                    [text]="true"
                    [rounded]="true"
                    (click)="openConfirm($event, eq, 'archive')"
                    pTooltip="Archive"
                    tooltipPosition="top"
                  />
                } @else {
                  <p-button
                    icon="pi pi-refresh"
                    severity="secondary"
                    size="small"
                    [text]="true"
                    [rounded]="true"
                    (click)="openConfirm($event, eq, 'unarchive')"
                    pTooltip="Unarchive"
                    tooltipPosition="top"
                  />
                }
              </div>
            </div>
            @if (eq.market) {
              <div class="text-sm text-muted-color">
                Market: {{ eq.market }}
              </div>
            }
            @if (eq.supplier) {
              <div class="text-sm text-muted-color">
                Supplier: {{ eq.supplier }}
              </div>
            }
            @if (displayMoney(eq.capex_cost) != null) {
              <div class="text-sm">
                CapEx:
                {{
                  displayMoney(eq.capex_cost)!
                    | currency: currencyCode ?? "" : "symbol" : "1.0-2"
                }}
              </div>
            }
          </div>

          <!-- Desktop Layout -->
          <div
            class="hidden lg:grid lg:items-center lg:gap-4 lg:px-4 lg:py-3"
            style="
              grid-template-columns: 2rem 1fr 2fr 1fr 1.5fr 1.5fr 1fr 1fr 1.5fr 1fr;
              min-width: 60rem;
            "
          >
            <div>
              <p-button
                [text]="true"
                severity="secondary"
                [rounded]="true"
                size="small"
                [icon]="
                  expandedRows.has(eq.id)
                    ? 'pi pi-chevron-down'
                    : 'pi pi-chevron-right'
                "
                (onClick)="toggleRow(eq.id)"
              />
            </div>
            <div class="text-sm">{{ eq.equipment_type || "—" }}</div>
            <div class="flex items-center gap-2">
              <span class="font-semibold">{{ eq.name }}</span>
              @if (eq.is_archived) {
                <p-chip label="Archived" styleClass="text-xs px-2 py-0" />
              }
            </div>
            <div class="text-sm">{{ eq.market || "—" }}</div>
            <div class="text-sm">
              @if (eq.supplier) {
                <p class="line-clamp-1 break-words">{{ eq.supplier }}</p>
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm">
              @if (eq.source) {
                <p class="line-clamp-1 break-words">{{ eq.source }}</p>
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm tabular-nums">
              @if (displayMoney(eq.capex_cost) != null) {
                {{
                  displayMoney(eq.capex_cost)!
                    | currency: currencyCode ?? "" : "symbol" : "1.0-2"
                }}
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm">
              @if (eq.amortization_years != null) {
                {{ eq.amortization_years }} yrs
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm">
              @if (eq.notes) {
                <p class="line-clamp-1 break-words">{{ eq.notes }}</p>
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="flex justify-center gap-1">
              @if (!eq.is_archived) {
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="openDrawer(eq)"
                  pTooltip="Edit"
                  tooltipPosition="top"
                />
                <p-button
                  icon="pi pi-box"
                  severity="secondary"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  (click)="openConfirm($event, eq, 'archive')"
                  pTooltip="Archive"
                  tooltipPosition="top"
                />
              } @else {
                <p-button
                  icon="pi pi-refresh"
                  severity="secondary"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  (click)="openConfirm($event, eq, 'unarchive')"
                  pTooltip="Unarchive"
                  tooltipPosition="top"
                />
              }
            </div>
          </div>

          <!-- Expanded Detail Cards -->
          @if (expandedRows.has(eq.id)) {
            <div
              class="px-6 py-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 border-t border-surface-switch-200 bg-surface-switch-100"
            >
              <div
                class="rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="flex items-center justify-center w-7 h-7 rounded-md bg-emerald-50"
                  >
                    <i class="pi pi-dollar text-emerald-600 text-sm"></i>
                  </span>
                  <h6
                    class="text-sm font-semibold text-surface-switch-700 uppercase tracking-wide"
                  >
                    Cost Metrics
                  </h6>
                </div>
                <div class="space-y-2">
                  <div
                    class="flex justify-between items-center py-1.5 border-b border-surface-switch-100"
                  >
                    <span class="text-xs text-muted-color">Amort / hr</span>
                    <span class="text-sm font-medium tabular-nums">
                      @if (displayMoney(eq.amortization_per_hour) != null) {
                        {{
                          displayMoney(eq.amortization_per_hour)!
                            | currency: currencyCode ?? "" : "symbol" : "1.2-2"
                        }}
                      } @else {
                        —
                      }
                    </span>
                  </div>
                  <div
                    class="flex justify-between items-center py-1.5 border-b border-surface-switch-100"
                  >
                    <span class="text-xs text-muted-color">Run cost / hr</span>
                    <span class="text-sm font-medium tabular-nums">
                      @if (displayMoney(eq.run_cost_per_hour) != null) {
                        {{
                          displayMoney(eq.run_cost_per_hour)!
                            | currency: currencyCode ?? "" : "symbol" : "1.2-2"
                        }}
                      } @else {
                        —
                      }
                    </span>
                  </div>
                  <div class="flex justify-between items-center py-1.5">
                    <span class="text-xs text-muted-color"
                      >Operating cost rate</span
                    >
                    <span class="text-sm font-medium tabular-nums">
                      @if (
                        displayMoney(eq.annual_operating_cost_rate) != null
                      ) {
                        {{
                          displayMoney(eq.annual_operating_cost_rate)! * 100
                            | number: "1.0-2"
                        }}%
                      } @else {
                        —
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="flex items-center justify-center w-7 h-7 rounded-md bg-red-50"
                  >
                    <i class="pi pi-chart-bar text-red-600 text-sm"></i>
                  </span>
                  <h6
                    class="text-sm font-semibold text-surface-switch-700 uppercase tracking-wide"
                  >
                    Capacity Metrics
                  </h6>
                </div>
                <div class="space-y-2">
                  <div
                    class="flex justify-between items-center py-1.5 border-b border-surface-switch-100"
                  >
                    <span class="text-xs text-muted-color"
                      >Availability hrs / yr</span
                    >
                    <span class="text-sm font-medium tabular-nums">
                      @if (
                        displayMoney(
                          eq.equipment_availability_hours_per_year
                        ) != null
                      ) {
                        {{
                          displayMoney(
                            eq.equipment_availability_hours_per_year
                          )! | number: "1.0-2"
                        }}
                      } @else {
                        —
                      }
                    </span>
                  </div>
                  <div
                    class="flex justify-between items-center py-1.5 border-b border-surface-switch-100"
                  >
                    <span class="text-xs text-muted-color"
                      >Max parts / process</span
                    >
                    <span class="text-sm font-medium tabular-nums">
                      @if (eq.max_parts_per_process != null) {
                        {{ eq.max_parts_per_process }}
                      } @else {
                        —
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="flex items-center justify-center w-7 h-7 rounded-md bg-amber-50"
                  >
                    <i class="pi pi-bolt text-amber-600 text-sm"></i>
                  </span>
                  <h6
                    class="text-sm font-semibold text-surface-switch-700 uppercase tracking-wide"
                  >
                    Space & Energy
                  </h6>
                </div>
                <div class="space-y-2">
                  <div
                    class="flex justify-between items-center py-1.5 border-b border-surface-switch-100"
                  >
                    <span class="text-xs text-muted-color">Footprint</span>
                    <span class="text-sm font-medium tabular-nums">
                      @if (displayMoney(eq.footprint_sq_ft) != null) {
                        {{
                          displayMoney(eq.footprint_sq_ft)! | number: "1.0-2"
                        }}
                        ft²
                      } @else {
                        —
                      }
                    </span>
                  </div>
                  <div
                    class="flex justify-between items-center py-1.5 border-b border-surface-switch-100"
                  >
                    <span class="text-xs text-muted-color">Power</span>
                    <span class="text-sm font-medium tabular-nums">
                      @if (displayMoney(eq.power_consumption_kwh) != null) {
                        {{
                          displayMoney(eq.power_consumption_kwh)!
                            | number: "1.0-2"
                        }}
                        kWh
                      } @else {
                        —
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="flex items-center justify-center w-7 h-7 rounded-md bg-blue-50"
                  >
                    <i class="pi pi-pen-to-square text-blue-600 text-sm"></i>
                  </span>
                  <h6
                    class="text-sm font-semibold text-surface-switch-700 uppercase tracking-wide"
                  >
                    Notes
                  </h6>
                </div>
                <div class="space-y-2">
                  <div class="pt-1">
                    <span class="text-xs text-muted-color block mb-1"
                      >Notes</span
                    >
                    @if (eq.notes) {
                      <p
                        class="text-sm text-surface-switch-700 leading-relaxed"
                      >
                        {{ eq.notes }}
                      </p>
                    } @else {
                      —
                    }
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
} @else if (hasActiveFilters) {
  <div class="text-center text-muted-color py-12">
    <i class="pi pi-filter-slash text-4xl mb-4 block"></i>
    <p class="mb-2">No equipment matches your filters.</p>
    <p-button
      label="Clear filters"
      icon="pi pi-times"
      severity="secondary"
      size="small"
      (onClick)="clearFilters()"
    />
  </div>
} @else {
  <div class="text-center text-muted-color">
    <p>No equipment</p>
  </div>
}

<shared-confirm-dialog />

<p-drawer
  [(visible)]="isDrawerVisible"
  [header]="equipmentInDraw ? 'Edit Equipment' : 'Add Equipment'"
  position="right"
  [styleClass]="'!w-full lg:!w-3/4 2xl:!w-1/2'"
  (onHide)="closeDrawer()"
>
  @if (saveError) {
    <p-message severity="error" styleClass="w-full mb-2">{{
      saveError
    }}</p-message>
  }
  <form [formGroup]="equipmentFormGroup" class="flex flex-col gap-5">
    @let drawerCurrency = currencyCode$ | async;
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="pt-1">
        <p-floatlabel variant="on">
          <input pInputText formControlName="equipmentType" class="w-full" />
          <label>Type</label>
        </p-floatlabel>
      </div>
      <div class="pt-1">
        <p-floatlabel variant="on" class="md:col-span-2">
          <input pInputText formControlName="name" class="w-full" />
          <label>Name</label>
        </p-floatlabel>
      </div>
      <div class="pt-1">
        <p-floatlabel variant="on">
          <input pInputText formControlName="market" class="w-full" />
          <label>Market</label>
        </p-floatlabel>
      </div>
      <div class="pt-1">
        <p-floatlabel variant="on">
          <input pInputText formControlName="supplier" class="w-full" />
          <label>Supplier</label>
        </p-floatlabel>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on" class="md:col-span-2">
          <input pInputText formControlName="source" class="w-full" />
          <label>Source / reference</label>
        </p-floatlabel>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="capexCost"
            [mode]="'currency'"
            [currency]="drawerCurrency || 'GBP'"
            locale="en-US"
            [min]="0"
            [minFractionDigits]="2"
            [maxFractionDigits]="2"
            class="w-full"
            inputStyleClass="w-full"
          />
          <label>CapEx cost</label>
        </p-floatlabel>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="amortizationYears"
            [useGrouping]="false"
            class="w-full"
            inputStyleClass="w-full"
          />
          <label>Amortization (years)</label>
        </p-floatlabel>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="annualOperatingCostRatePercent"
            mode="decimal"
            [min]="0"
            [step]="0.1"
            [minFractionDigits]="0"
            [maxFractionDigits]="2"
            suffix="%"
            [useGrouping]="false"
            class="w-full"
            inputStyleClass="w-full"
          />
          <label>Annual operating cost rate</label>
        </p-floatlabel>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="maxPartsPerProcess"
            [useGrouping]="false"
            class="w-full"
            inputStyleClass="w-full"
          />
          <label>Max parts / process</label>
        </p-floatlabel>
      </div>

      <div
        class="md:col-span-2 rounded-lg border border-surface-switch-200 bg-surface-switch-50 p-4"
      >
        <h6 class="m-0 mb-3 text-sm font-semibold text-surface-switch-700">
          Estimated values using settings defaults
        </h6>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div class="flex flex-col gap-1">
            <span class="text-xs text-muted-color"
              >Availability (hrs / yr)</span
            >
            <span class="font-medium tabular-nums">
              @if (calculatedEquipmentAvailabilityHoursPerYear != null) {
                {{
                  calculatedEquipmentAvailabilityHoursPerYear | number: "1.0-2"
                }}
              } @else {
                <span class="text-muted-color">—</span>
              }
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-muted-color">Amortization / hr</span>
            <span class="font-medium tabular-nums">
              @if (calculatedAmortizationPerHour != null) {
                {{
                  calculatedAmortizationPerHour
                    | currency: drawerCurrency ?? "" : "symbol" : "1.2-2"
                }}
              } @else {
                <span class="text-muted-color">—</span>
              }
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-muted-color">Run cost / hr</span>
            <span class="font-medium tabular-nums">
              @if (calculatedRunCostPerHour != null) {
                {{
                  calculatedRunCostPerHour
                    | currency: drawerCurrency ?? "" : "symbol" : "1.2-2"
                }}
              } @else {
                <span class="text-muted-color">—</span>
              }
            </span>
          </div>
        </div>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="footprintSqFt"
            mode="decimal"
            [min]="0"
            [minFractionDigits]="0"
            [maxFractionDigits]="2"
            [useGrouping]="false"
            class="w-full"
            inputStyleClass="w-full"
          />
          <label>Footprint (sq ft)</label>
        </p-floatlabel>
      </div>

      <div class="pt-1">
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="powerConsumptionKwh"
            mode="decimal"
            [min]="0"
            [minFractionDigits]="0"
            [maxFractionDigits]="2"
            [useGrouping]="false"
            class="w-full"
            inputStyleClass="w-full"
          />
          <label>Power (kWh)</label>
        </p-floatlabel>
      </div>
    </div>
    <div class="flex flex-col gap-2">
      <label for="notes" class="text-sm font-medium">Notes</label>

      <textarea
        id="notes"
        pTextarea
        formControlName="notes"
        class="w-full"
        rows="3"
      ></textarea>
    </div>
    <div class="flex gap-2 justify-end">
      <p-button
        label="Cancel"
        severity="secondary"
        (onClick)="closeDrawer()"
      ></p-button>
      <p-button label="Save" (onClick)="saveEquipment()"></p-button>
    </div>
  </form>
</p-drawer>
